import type { ThresholdStatus } from "@/components/dashboard";
import type { MetricTone } from "@/components/dashboard";
import type { WebVitalMetric } from "@/lib/validation";

export interface VitalThreshold {
  good: number;
  poor: number;
  max: number;
  unit: string;
  fractionDigits: number;
}

export const VITAL_THRESHOLDS: Record<WebVitalMetric, VitalThreshold> = {
  web_vitals_lcp: { good: 2500, poor: 4000, max: 6000, unit: "ms", fractionDigits: 0 },
  web_vitals_inp: { good: 200, poor: 500, max: 800, unit: "ms", fractionDigits: 0 },
  web_vitals_cls: { good: 0.1, poor: 0.25, max: 0.5, unit: "", fractionDigits: 2 },
  web_vitals_ttfb: { good: 800, poor: 1800, max: 3000, unit: "ms", fractionDigits: 0 },
};

export function classifyVital(
  metric: WebVitalMetric,
  value: number | null,
): { status: ThresholdStatus; markerPct: number | null } {
  if (value === null || !Number.isFinite(value)) {
    return { status: "unknown", markerPct: null };
  }
  const t = VITAL_THRESHOLDS[metric];
  const status: ThresholdStatus =
    value <= t.good ? "good" : value <= t.poor ? "warn" : "bad";
  const markerPct = Math.min(100, (value / t.max) * 100);
  return { status, markerPct };
}

export function pickConversionTone(pct: number | null | undefined): MetricTone {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return "neutral";
  if (pct >= 10) return "emerald";
  if (pct >= 5) return "amber";
  return "red";
}

export function pickRateTone(
  pct: number | null | undefined,
  thresholds: { good: number; warn: number } = { good: 99, warn: 95 },
): MetricTone {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return "neutral";
  if (pct >= thresholds.good) return "emerald";
  if (pct >= thresholds.warn) return "amber";
  return "red";
}

export function pickErrorRateTone(
  pct: number | null | undefined,
  thresholds: { good: number; warn: number } = { good: 1, warn: 5 },
): MetricTone {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return "neutral";
  if (pct <= thresholds.good) return "emerald";
  if (pct <= thresholds.warn) return "amber";
  return "red";
}
